import { useEffect, useState } from "react";
import Button from "./ui/Button.jsx";
import {
  mapOryConnectApiError,
  normalizeOryProjectSlugInput,
  validateOryConnectFields,
} from "./onboarding/oryConnectValidation.js";
import { ORY_NETWORK_GUIDANCE } from "./onboarding/oryConnectGuidance.js";
import { getProjectTenantDisplayUrl } from "../utils/projectProvider.js";

/**
 * Credential-update surface for existing Ory Network projects (POST-ARC.12a).
 */

/**
 * @param {{ open: boolean, onClose: () => void, project: { projectId?: string, org?: { url?: string } } | null, onConnected?: () => void }} props
 */
export default function ConnectOryModal({ open, onClose, project, onConnected }) {
  const projectId = project?.projectId;
  const [projectSlug, setProjectSlug] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState(null);
  const [testOk, setTestOk] = useState(false);
  const [connectedAs, setConnectedAs] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const url = getProjectTenantDisplayUrl(project) || "";
    setProjectSlug(normalizeOryProjectSlugInput(url));
    setApiKey("");
    setError(null);
    setTestOk(false);
    setConnectedAs("");
  }, [open, project]);

  if (!open) return null;

  const bodyPayload = () => ({
    projectId,
    project_slug: projectSlug.trim(),
    api_key: apiKey.trim(),
  });

  const handleTest = async () => {
    setError(null);
    setTestOk(false);
    setConnectedAs("");
    const validated = validateOryConnectFields({
      project_slug: projectSlug,
      api_key: apiKey,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    if (!projectId) {
      setError("Missing project.");
      return;
    }
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/ory/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setError(mapOryConnectApiError(data));
        return;
      }
      setTestOk(true);
      setConnectedAs(String(data.connected_as || data.project_slug || validated.project_slug));
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const handleConnect = async () => {
    if (!testOk || !projectId) return;
    setError(null);
    setConnectBusy(true);
    try {
      const res = await fetch("/api/idp/ory/connect", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(mapOryConnectApiError(data));
      }
      onConnected?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || "Connect failed");
    } finally {
      setConnectBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(5, 8, 16, 0.72)",
        display: "grid",
        placeItems: "center",
        padding: "24px",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-ory-title"
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "auto",
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "10px",
          padding: "24px",
        }}
      >
        <h2 id="connect-ory-title" style={{ fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>
          Connect Ory Network project
        </h2>
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
          {ORY_NETWORK_GUIDANCE}
        </p>

        <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }}>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">Project slug</span>
            <input
              type="text"
              value={projectSlug}
              onChange={(e) => {
                setProjectSlug(e.target.value);
                setTestOk(false);
              }}
              placeholder="your-project-slug"
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid var(--border)",
                color: "var(--text-primary)",
                background: "var(--bg-input)",
              }}
            />
          </label>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">Project API key</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type={showSecret ? "text" : "password"}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setTestOk(false);
                }}
                autoComplete="off"
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid var(--border)",
                  color: "var(--text-primary)",
                  background: "var(--bg-input)",
                }}
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                style={{ fontSize: "12px", padding: "0 10px" }}
              >
                {showSecret ? "Hide" : "Show"}
              </button>
            </div>
          </label>
        </div>

        {testOk ? (
          <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
            ✓ Connection verified — Connected as: {connectedAs}
          </p>
        ) : null}

        {error ? (
          <p style={{ fontSize: "13px", color: "var(--status-error, #f87171)", marginBottom: "12px" }}>{error}</p>
        ) : null}

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="secondary" disabled={testBusy} onClick={() => void handleTest()}>
            {testBusy ? "Testing…" : "Test Connection"}
          </Button>
          <Button type="button" disabled={!testOk || connectBusy} onClick={() => void handleConnect()}>
            {connectBusy ? "Saving…" : "Save credentials"}
          </Button>
        </div>
      </div>
    </div>
  );
}
